import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './home.scss';
import { dashboardService, DashboardData } from '../../services/dashboardService';
import { adminApi } from '../../utils/api';
import { DashboardStats } from '../../components/dashboard/DashboardStats';
import { InvoiceStatusChart } from '../../components/dashboard/InvoiceStatusChart';
import { InvoiceAmountChart } from '../../components/dashboard/InvoiceAmountChart';
import { OrderStatusChart } from '../../components/dashboard/OrderStatusChart';
import { OrderTypeChart } from '../../components/dashboard/OrderTypeChart';
import { RevenueChart } from '../../components/dashboard/RevenueChart';

export const Home = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iqdRate, setIqdRate] = useState<number | null>(null);
  const [iqdRateDate, setIqdRateDate] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
    loadIqdRate();
  }, []);

  const loadIqdRate = async () => {
    try {
      const response = await adminApi.call('/exchangerate/iqd-latest', 'GET');
      if (response.success && response.data) {
        setIqdRate(response.data.rate ?? null);
        setIqdRateDate(response.data.date ?? null);
      }
    } catch (err) {
      console.error('Error loading IQD rate:', err);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardData = await dashboardService.getDashboardData();
      setData(dashboardData);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(t('common.error') || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error">{error}</div>
        <button onClick={loadDashboardData} className="btn btn-primary">
          {t('buttons.retry')}
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dashboard-container">
        <div className="loading">{t('common.noData')}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1>{t('dashboard:title')}</h1>

      {/* Summary Stats */}
      <DashboardStats
        totalInvoices={data.invoiceStats.totalInvoices}
        totalAmount={data.invoiceStats.totalAmount}
        totalOrders={data.orderStats.totalOrders}
        totalShipForMe={data.orderStats.totalShipForMe}
        totalRevenue={data.orderStats.totalRevenue}
        averageOrderValue={data.orderStats.averageOrderValue}
        iqdRate={iqdRate}
        iqdRateDate={iqdRateDate}
      />

      {/* Charts Grid */}
      <div className="charts-grid">
        <InvoiceStatusChart data={data.invoiceStats.invoicesByStatus} />
        <InvoiceAmountChart data={{
          collectedAmount: data.invoiceStats.collectedAmount,
          receivedAmount: data.invoiceStats.receivedAmount,
          pendingAmount: data.invoiceStats.pendingAmount,
        }} />
      </div>

      <div className="charts-grid">
        <OrderStatusChart data={data.orderStats.ordersByStatus} />
        <RevenueChart
          totalRevenue={data.orderStats.totalRevenue}
          averageOrderValue={data.orderStats.averageOrderValue}
        />
      </div>

      <div className="charts-grid">
        <OrderTypeChart
          totalOrders={data.orderStats.totalOrders}
          totalShipForMe={data.orderStats.totalShipForMe}
        />
      </div>
    </div>
  );
}
